import React, { useState, useEffect, useRef } from 'react';
import { countryCodes, getCountryByCode, validatePhoneForCountry, parsePhoneWithCountryCode, stripNonDigits } from '../utils/phoneValidation';
import { FiChevronDown } from 'react-icons/fi';

/**
 * A reusable phone input component with country code selector and validation.
 *
 * Props:
 * - name (string): field name for the phone number
 * - value (string): the full phone value (e.g. "+91 9876543210")
 * - countryCodeValue (string): optional separate country code state (ISO code like 'US')
 * - onChange (fn): called with (name, fullPhoneValue, countryCode)
 * - darkMode (boolean): dark mode toggle
 * - error (string): external validation error message to display
 * - required (boolean): whether the field is required
 * - label (string): optional label text
 * - disabled (boolean): whether the input is disabled
 */
const CountryPhoneInput = ({
  name,
  value = '',
  countryCodeValue,
  onChange,
  darkMode = false,
  error = '',
  required = false,
  label = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localError, setLocalError] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Parse the existing value to extract country code and local number
  const parsed = parsePhoneWithCountryCode(value);
  const selectedCode = countryCodeValue || parsed.countryCode;
  const selectedCountry = getCountryByCode(selectedCode);
  const localNumber = parsed.localNumber;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredCountries = countryCodes.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.dialCode.includes(searchQuery) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCountrySelect = (country) => {
    setIsOpen(false);
    setSearchQuery('');
    const fullValue = localNumber ? `${country.dialCode} ${localNumber}` : '';
    onChange(name, fullValue, country.code);
    // Re-validate with new country
    if (localNumber) {
      const result = validatePhoneForCountry(localNumber, country.code);
      setLocalError(result.valid ? '' : result.message);
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    // Allow digits, spaces, hyphens, parentheses
    const sanitized = input.replace(/[^\d\s()-]/g, '');
    const fullValue = sanitized ? `${selectedCountry.dialCode} ${sanitized}` : '';
    onChange(name, fullValue, selectedCode);

    // Validate on change
    if (sanitized) {
      const result = validatePhoneForCountry(sanitized, selectedCode);
      setLocalError(result.valid ? '' : result.message);
    } else {
      setLocalError('');
    }
  };

  const displayError = error || localError;

  const inputBorderClass = displayError
    ? 'border-red-500 focus:ring-red-500'
    : darkMode
    ? 'border-slate-600 focus:ring-blue-500'
    : 'border-slate-200 focus:ring-blue-500';

  return (
    <div>
      {label && (
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
          {label}{required && ' *'}
        </label>
      )}
      <div className="flex gap-0">
        {/* Country Code Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-1 px-3 py-2.5 border text-sm whitespace-nowrap focus:outline-none focus:ring-2 ${inputBorderClass} ${
              darkMode
                ? 'bg-slate-700 text-white hover:bg-slate-600'
                : 'bg-slate-50 text-slate-800 hover:bg-slate-100'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-base">{getFlagEmoji(selectedCountry.code)}</span>
            <span className="font-medium">{selectedCountry.dialCode}</span>
            <FiChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className={`absolute z-50 mt-1 w-72 max-h-60 overflow-y-auto rounded-lg shadow-lg border ${
              darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
            }`}>
              {/* Search */}
              <div className="sticky top-0 p-2 border-b border-slate-200 dark:border-slate-600">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search country..."
                  className={`w-full px-3 py-1.5 text-sm border focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    darkMode
                      ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400'
                      : 'bg-white border-slate-200 text-slate-800'
                  }`}
                />
              </div>
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    country.code === selectedCode
                      ? darkMode
                        ? 'bg-blue-600/20 text-blue-300'
                        : 'bg-blue-50 text-blue-700'
                      : darkMode
                      ? 'text-slate-300 hover:bg-slate-600'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-base">{getFlagEmoji(country.code)}</span>
                  <span className="flex-1">{country.name}</span>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{country.dialCode}</span>
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <div className={`px-3 py-4 text-sm text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  No countries found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Phone Input */}
        <input
          type="tel"
          value={localNumber}
          onChange={handlePhoneChange}
          disabled={disabled}
          placeholder={selectedCountry.placeholder}
          className={`flex-1 px-4 py-2.5 border-t border-b border-r  text-sm focus:outline-none focus:ring-2 ${inputBorderClass} ${
            darkMode
              ? 'bg-slate-700 text-white placeholder-slate-400'
              : 'bg-white text-slate-800'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>
      {displayError && (
        <p className="text-red-500 text-xs mt-1">{displayError}</p>
      )}
      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        {selectedCountry.minDigits === selectedCountry.maxDigits
          ? `${selectedCountry.minDigits} digits required for ${selectedCountry.name}`
          : `${selectedCountry.minDigits}-${selectedCountry.maxDigits} digits required for ${selectedCountry.name}`}
      </p>
    </div>
  );
};

/**
 * Converts an ISO country code to a flag emoji.
 * Works by mapping each letter to a Regional Indicator Symbol.
 */
function getFlagEmoji(countryCode) {
  if (!countryCode) return '🏳️';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default CountryPhoneInput;
